import { useMemo, CSSProperties, useCallback } from 'react'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { EffectScatterChart, ScatterChart } from 'echarts/charts'
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers'

import {
	TooltipComponent,
	GridComponent
} from 'echarts/components'

import { CompareRecord, Disease } from 'features/dpcResult/types'
import { numericValueFormat } from 'features/dashBoard/components/graph/graphs/common'
import { SisetuResult } from 'interface/sisetu'

echarts.use([
	TooltipComponent,
	GridComponent,
  ScatterChart,
	EffectScatterChart,
	CanvasRenderer,
	LabelLayout,
  UniversalTransition
])

const toolTipMargin = { x: 20, y: 10 }

type DpcCompareGraphProps = {
	disease: Disease | undefined
	sisetu: SisetuResult
	results: CompareRecord[]
	width?: number | 'auto'
	height?: number | 'auto'
	style?: CSSProperties
}
export const DpcCompareGraph = ({
	disease,
	sisetu,
	results,
	width,
	height,
	style
}: DpcCompareGraphProps) => {
	type PlotData = {
		bedCount: number
		cases: number
		code: string
		label: string
		name: string
		rank: number
	}
	const { casesMax, bedCountMax, currentData, otherData } = useMemo(
		() => {
			const dpcCode = disease?.code
			if (!dpcCode || results.length === 0) {
				return { casesMax: 0, bedCountMax: 0, currentData: [], otherData: [] }
			}
			let casesMax = 0
			let bedCountMax = 0
			const currentData: PlotData[] = []
			const otherData: PlotData[] = []
			results.forEach((result, i) => {
				const plot: PlotData = {
					bedCount: result.sisetu.bedCount?.total ?? 0,
					cases: result.cases,
					code: result.sisetu.sisetuCode,
					label: result.sisetu.ryakusikiSisetuName ?? result.sisetu.sisetuName ?? '',
					name: result.sisetu.sisetuName ?? '',
					rank: i + 1
				}
				if (result.sisetu.sisetuCode === sisetu.sisetuCode) {
					currentData.push(plot)
				} else {
					otherData.push(plot)
				}
				casesMax = Math.max(casesMax, plot.cases)
				bedCountMax = Math.max(bedCountMax, plot.bedCount)
			})
			return {
				currentData,
				otherData,
				casesMax,
				bedCountMax
			}
		},
		[results, disease, sisetu]
	)

	const toolTipFormatter = useCallback(
		(obj: { value: number[] }) => {
			const value = obj.value
			return (
				`${value[4] ?? ''}<br>` +
				`順位 : ${numericValueFormat(value[5], 1, "位")}<br>` +
				`病床数 : ${numericValueFormat(value[0], 1, "床")}<br>` +
				`${disease?.name ?? '***'}: ${numericValueFormat(value[1], 1, "件")}<br>`
			)
		},
		[disease]
	)
	const tooltipPosition = useCallback(
		(pos: number[], params: unknown, dom: unknown, rect: unknown, size: { viewSize: number[], contentSize: number[] }) => {
			const left = Math.max(toolTipMargin.y, Math.min(size.viewSize[0] - (size.contentSize[0] + toolTipMargin.y), 
				(pos[0] < size.viewSize[0] / 2) ? (pos[0] + toolTipMargin.x) : (pos[0] - (size.contentSize[0] + toolTipMargin.x))
			))
			const top = Math.max(toolTipMargin.y, Math.min(size.viewSize[1] - (size.contentSize[1] + toolTipMargin.y), 
				(pos[1] < size.viewSize[1] / 2) ? (pos[1] + toolTipMargin.y) : (pos[1] - (size.contentSize[1] + toolTipMargin.y))
			))
			return { top, left }
		},
		[]
	)

	const labelFormatter = useCallback(
		(param: { data: string[] }) => {
			return param.data[3]
		},
		[]
	)
	return (
		<ReactEChartsCore
			echarts={echarts}
			style={style}
			notMerge={true}
			option={{
				//animation: false,
				tooltip: {
					formatter: toolTipFormatter,
					position: tooltipPosition
				},
				grid: {
					top: 10,
					left: 30,
					right: 30,
					bottom: 30,
					containLabel: true
				},
				yAxis: {
					name: disease?.name ?? '***',
					nameMoveOverlap: true,
					nameLocation: 'middle',
					type: 'value',
					min: 0,
					max: casesMax,
					axisLabel: {
						formatter: '{value} 件',
					}
				},
				xAxis: {
					name: '病床数',
					nameMoveOverlap: true,
					nameLocation: 'middle',
					type: 'value',
					min: 0,
					max: bedCountMax,
					axisLabel: {
						formatter: '{value} 床',
					}
				},
				series: [{
					type: 'scatter',
					name: 'other',
					label: {
						show: true,
						position: 'bottom',
						formatter: labelFormatter
					},
					emphasis: {
						focus: 'self'
					},
					data: otherData.map(data => [
						data.bedCount, data.cases,
						data.code, data.label, data.name, data.rank
					]),
					z: 1
				}, {
					type: 'effectScatter',
					symbolSize: 20,
					name: 'current',
					data: currentData.map(data => [
						data.bedCount, data.cases,
						data.code, data.label, data.name, data.rank
					]),
					label: {
						show: true,
						position: 'bottom',
						textStyle: {
							fontWeight: 'bold',
							fontSize: 14
						},
						zIndex: 11,
						formatter: labelFormatter
					},
					z: 10
				}],
				textStyle: {
					fontFamily: 'Noto Sans JP,sans-serif'
				}
			}}
			opts={{
				renderer: 'canvas',
				width: width,
				height: height,
			}}
			theme="insite"
		/>
	)
}
